"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import { Home, ChevronRight, Phone, Mail, ExternalLink } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import OtrasOpciones from "../components/OtrasOpciones";
import ExternalLinkModal from "@/components/ExternalLinkModal/ExternalLinkModal";
import MapButton from "../../../components/MapButton";

const NAVY = "#102542";
const YELLOW = "#ffb800";

const HERO_IMAGE =
  "https://galeria.tocumenaero.com/gallery/api/MetroBus/321A8648.jpg";

const RUTAS_URL = "https://www.mibus.com.pa/red-de-rutas/";

const TARJETA_T1_MAP =
  "https://airportpanama.lmp.prod.livingmap.com/poi?feature=4d91c87db13f4edface806f7f11923fd&floor=1&lang=en-GB#18.19/9.067183/-79.387281/119";
const TARJETA_T2_MAP =
  "https://airportpanama.lmp.prod.livingmap.com/poi?feature=df7b62a133816496dc36648f72fcce30&floor=1&lang=en-GB&name-search=enable&query=Travel%20Data%20Global#18.13/9.062831/-79.390513/119";

const PARADAS_T1_MAP =
  "https://airportpanama.lmp.prod.livingmap.com/poi?feature=5a21369811dc0ade85b9fae130ba7bfd&floor=1&lang=en-GB#18.13/9.067902/-79.386986/119";
const PARADAS_T2_MAP =
  "https://airportpanama.lmp.prod.livingmap.com/poi?feature=c5cbfdc0a304aa979bf7cfa348ce15ca&floor=1&lang=en-GB&name-search=enable&query=Travel%20Data%20Global#18.13/9.063482/-79.390519/119";

const TARJETA_T1_IMG =
  "https://galeria.tocumenaero.com/gallery/Concesiones/Travel%20Data/01KEYSPKW0TDJ5NHCNQYAF4GR0.jpg";
const TARJETA_T2_IMG =
  "https://galeria.tocumenaero.com/gallery/Concesiones/Travel%20Data/01KEYYCMYJ11N87WY6EWJH2CRY.jpg";
const PARADAS_T1_IMG =
  "https://galeria.tocumenaero.com/gallery/aeropuerto/aeropuerto054.jpg";
const PARADAS_T2_IMG =
  "https://galeria.tocumenaero.com/gallery/aeropuerto/aeropuerto036.jpg";

type StatItem = { label: string; value: string; note: string };

type TerminalCardProps = {
  image: string;
  label: string;
  cta: string;
  mapSrc: string;
};

function TerminalCard({ image, label, cta, mapSrc }: TerminalCardProps) {
  return (
    <article className="group relative bg-white grid grid-cols-2 items-stretch transition-colors hover:bg-gray-50 overflow-hidden min-h-[220px]">
      <span
        className="absolute top-0 left-0 right-0 h-0.5 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 z-10"
        style={{ backgroundColor: YELLOW }}
      />
      <div className="relative w-full h-full overflow-hidden bg-gray-100">
        <Image
          src={image}
          alt={label}
          fill
          sizes="(max-width: 768px) 50vw, 25vw"
          className="object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="p-6 flex flex-col justify-center min-w-0">
        <span className="text-[10px] font-mono uppercase tracking-wider text-gray-400 mb-1">
          Terminal
        </span>
        <h4
          className="text-base font-bold tracking-tight mb-3"
          style={{ color: NAVY }}
        >
          {label}
        </h4>
        <MapButton src={mapSrc} variant="link" label={cta} />
      </div>
    </article>
  );
}

export default function MetroBusPage() {
  const t = useTranslations("metrobusPage");
  const tCrumb = useTranslations("breadcrumb");
  const tTransporte = useTranslations("nav.transporte");
  const locale = useLocale();

  const [showRutasModal, setShowRutasModal] = useState(false);

  const stats = t.raw("stats.items") as StatItem[];

  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden">
        <Image
          src={HERO_IMAGE}
          alt={t("hero.title")}
          fill
          priority
          sizes="100vw"
          className="object-cover blur-xs"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />

        <div className="relative z-10 max-w-[80%] mx-auto w-full pb-16 md:pb-20">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-xs text-white/80 flex-wrap">
              <li>
                <Link
                  href={`/${locale}`}
                  className="inline-flex items-center gap-1.5 hover:text-white transition-colors"
                >
                  <Home className="w-3.5 h-3.5" />
                  <span>{tCrumb("home")}</span>
                </Link>
              </li>
              <li aria-hidden>
                <ChevronRight className="w-3.5 h-3.5 text-white/40" />
              </li>
              <li>
                <Link
                  href={`/${locale}/transporte`}
                  className="hover:text-white transition-colors"
                >
                  {tTransporte("label")}
                </Link>
              </li>
              <li aria-hidden>
                <ChevronRight className="w-3.5 h-3.5 text-white/40" />
              </li>
              <li
                aria-current="page"
                className="text-white font-medium truncate"
              >
                {t("breadcrumb")}
              </li>
            </ol>
          </nav>

          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white font-semibold mb-4">
              <span className="w-8 h-px" style={{ backgroundColor: YELLOW }} />
              {t("hero.eyebrow")}
            </span>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight text-balance">
              {t("hero.title")}
            </h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed">
              {t("hero.subtitle")}
            </p>
          </div>
        </div>
      </section>

      {/* Dónde comprar tu tarjeta de abordaje */}
      <section className="bg-white">
        <div className="max-w-[80%] mx-auto py-24">
          <div className="max-w-3xl mb-14">
            <span
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold mb-4"
              style={{ color: NAVY }}
            >
              <span className="w-8 h-px" style={{ backgroundColor: NAVY }} />
              {t("tarjeta.eyebrow")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
              {t("tarjeta.title")}
            </h2>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              {t("tarjeta.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
            <TerminalCard
              image={TARJETA_T1_IMG}
              label={t("tarjeta.t1Label")}
              cta={t("tarjeta.verEnMapa")}
              mapSrc={TARJETA_T1_MAP}
            />
            <TerminalCard
              image={TARJETA_T2_IMG}
              label={t("tarjeta.t2Label")}
              cta={t("tarjeta.verEnMapa")}
              mapSrc={TARJETA_T2_MAP}
            />
          </div>
        </div>
      </section>

      {/* Ver paradas en el mapa */}
      <section className="bg-gray-50 border-y border-gray-200">
        <div className="max-w-[80%] mx-auto py-24">
          <div className="max-w-3xl mb-14">
            <span
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold mb-4"
              style={{ color: NAVY }}
            >
              <span className="w-8 h-px" style={{ backgroundColor: NAVY }} />
              {t("paradas.eyebrow")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
              {t("paradas.title")}
            </h2>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              {t("paradas.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
            <TerminalCard
              image={PARADAS_T1_IMG}
              label={t("paradas.t1Label")}
              cta={t("paradas.verEnMapa")}
              mapSrc={PARADAS_T1_MAP}
            />
            <TerminalCard
              image={PARADAS_T2_IMG}
              label={t("paradas.t2Label")}
              cta={t("paradas.verEnMapa")}
              mapSrc={PARADAS_T2_MAP}
            />
          </div>
        </div>
      </section>

      {/* Stats: tarifa, trasbordos, horario */}
      <section className="bg-white">
        <div className="max-w-[80%] mx-auto py-24">
          <div className="max-w-3xl mb-14">
            <span
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold mb-4"
              style={{ color: NAVY }}
            >
              <span className="w-8 h-px" style={{ backgroundColor: NAVY }} />
              {t("stats.eyebrow")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
              {t("stats.title")}
            </h2>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              {t("stats.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
            {stats.map((s, idx) => (
              <article
                key={s.label}
                className="bg-white p-8 flex flex-col transition-colors hover:bg-gray-50"
              >
                <span className="text-[11px] font-mono uppercase tracking-wider text-gray-400 tabular-nums mb-6">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <p
                  className="text-3xl md:text-4xl font-bold tabular-nums tracking-tight mb-3"
                  style={{ color: NAVY }}
                >
                  {s.value}
                </p>
                <h3 className="text-sm font-bold text-gray-900 tracking-tight mb-1">
                  {s.label}
                </h3>
                <p className="text-xs text-gray-500 leading-relaxed">
                  {s.note}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Rutas CTA */}
      <section className="bg-gray-50 border-y border-gray-200">
        <div className="max-w-[80%] mx-auto py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7 max-w-2xl">
              <span
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold mb-4"
                style={{ color: NAVY }}
              >
                <span className="w-8 h-px" style={{ backgroundColor: NAVY }} />
                {t("rutas.eyebrow")}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
                {t("rutas.title")}
              </h2>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-8">
                {t("rutas.subtitle")}
              </p>

              <button
                type="button"
                onClick={() => setShowRutasModal(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs uppercase tracking-wider font-semibold transition-colors hover:opacity-90"
                style={{ backgroundColor: YELLOW, color: NAVY }}
              >
                {t("rutas.cta")}
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="lg:col-span-5 lg:col-start-8 relative aspect-[4/3] rounded-2xl overflow-hidden border border-gray-200">
              <Image
                src="https://galeria.tocumenaero.com/gallery/api/MetroBus/321A8637.jpg"
                alt={t("rutas.title")}
                fill
                sizes="(max-width: 1024px) 100vw, 40vw"
                className="object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contacto */}
      <section className="bg-[#102542]">
        <div className="max-w-[80%] mx-auto py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-xl">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white font-semibold mb-4">
                <span
                  className="w-8 h-px"
                  style={{ backgroundColor: YELLOW }}
                />
                {t("contacto.eyebrow")}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
                {t("contacto.title")}
              </h2>
              <p className="text-white/70 text-base md:text-lg leading-relaxed">
                {t("contacto.subtitle")}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
              <a
                href="tel:311"
                className="bg-[#102542] p-6 flex items-center gap-4 transition-colors hover:bg-[#0a1a30] group"
              >
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: "rgba(255,184,0,0.15)",
                    color: YELLOW,
                  }}
                >
                  <Phone className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-white/50 mb-0.5">
                    {t("contacto.callLabel")}
                  </p>
                  <p className="text-white font-semibold tabular-nums">311</p>
                </div>
              </a>

              <a
                href="https://wa.me/50767812906"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#102542] p-6 flex items-center gap-4 transition-colors hover:bg-[#0a1a30] group"
              >
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: "rgba(255,184,0,0.15)",
                    color: YELLOW,
                  }}
                >
                  <FaWhatsapp className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-white/50 mb-0.5">
                    {t("contacto.whatsappLabel")}
                  </p>
                  <p className="text-white font-semibold tabular-nums">
                    (+507) 6781-2906
                  </p>
                </div>
              </a>

              <a
                href="mailto:servicioalcliente@mibus.com.pa"
                className="bg-[#102542] p-6 flex items-center gap-4 transition-colors hover:bg-[#0a1a30] group"
              >
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: "rgba(255,184,0,0.15)",
                    color: YELLOW,
                  }}
                >
                  <Mail className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-white/50 mb-0.5">
                    {t("contacto.emailLabel")}
                  </p>
                  <p className="text-white font-semibold truncate">
                    servicioalcliente@mibus.com.pa
                  </p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Otras opciones */}
      <div className="max-w-[80%] mx-auto">
        <OtrasOpciones actual="metrobus" />
      </div>

      <ExternalLinkModal
        isOpen={showRutasModal}
        onClose={() => setShowRutasModal(false)}
        onContinue={() => {
          window.open(RUTAS_URL, "_blank");
          setShowRutasModal(false);
        }}
      />
    </main>
  );
}
